/**
 * AssetsLibrary - main view for managing client brand assets.
 *
 * Single flat grid of all assets (round and freestyle mixed).
 * Category is visually obvious from thumbnails. Round assets
 * get a "Generate Sizes" action on their card.
 */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { Button } from "@/components/ui/Button";
import { ImageIcon } from "@/components/icons";
import { TabGroup } from "@/components/ui/TabGroup";
import { Text } from "@/components/ui/Text";
import { ScrollableContainer } from "@/components/MediaContainer";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";
import {
  ContentType,
  CONTENT_TYPE_TABS,
  CONTENT_TYPE_TAB_CONFIG,
} from "@/domains/LibraryView/components/LibraryLayout";
import {
  useClientAssets,
  useDeleteAsset,
  useUpdateAssetTags,
  downloadSizes,
} from "./hooks/useClientAssets";
import { AssetCard } from "./components/AssetCard";
import { AssetUploadModal } from "./components/AssetUploadModal";

export const AssetsLibrary: React.FC = () => {
  const { selectedClient } = useAppStore();
  const navigate = useNavigate();
  const clientId = selectedClient?.id;

  const { data, isLoading, isError } = useClientAssets(clientId);
  const deleteMutation = useDeleteAsset();
  const updateTagsMutation = useUpdateAssetTags();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const allAssets = useMemo(() => {
    const round = data?.round ?? [];
    const freestyle = data?.freestyle ?? [];
    return [...round, ...freestyle];
  }, [data]);

  const handleContentTypeChange = (contentType: ContentType) => {
    const pathMap: Record<ContentType, string> = {
      images: "../photos",
      "post-videos": "../post-videos",
      stories: "../stories",
      reels: "../reels",
      videos: "../videos",
      audio: "../audio",
      assets: "../assets",
    };
    navigate(pathMap[contentType]);
  };

  const handleDelete = (assetId: number) => {
    if (!clientId) return;
    deleteMutation.mutate({ clientId, assetId });
  };

  const handleTagsChange = (assetId: number, tags: string[]) => {
    if (!clientId) return;
    updateTagsMutation.mutate({ clientId, assetId, tags });
  };

  const handleGenerateSizes = async (assetId: number) => {
    if (!clientId) return;
    try {
      await downloadSizes(clientId, assetId);
    } catch (err) {
      console.error("Failed to download sizes:", err);
    }
  };

  if (!selectedClient || !clientId) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <div className="text-center">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <Text variant="h3">No client selected</Text>
          <Text variant="bodySmall" color="muted" className="mt-1">
            Select a client to manage their brand assets
          </Text>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="inline-block">
              <TabGroup
                items={CONTENT_TYPE_TABS}
                activeId={"assets" as ContentType}
                onTabChange={handleContentTypeChange}
                config={CONTENT_TYPE_TAB_CONFIG}
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUploadModalOpen(true)}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload
            </Button>
          </div>
        </div>
      </PageHeader>

      <PageSection.Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 animate-spin text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <Text variant="h3" className="mb-2">Loading assets...</Text>
            </div>
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-16">
            <Text variant="body" color="red" weight="medium">
              Failed to load assets
            </Text>
          </div>
        ) : allAssets.length > 0 ? (
          <ScrollableContainer height="100%" padding="none">
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3 p-4">
              {allAssets.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  clientId={clientId}
                  onDelete={handleDelete}
                  onTagsChange={handleTagsChange}
                  onGenerateSizes={
                    asset.category === "round" ? handleGenerateSizes : undefined
                  }
                />
              ))}
            </div>
          </ScrollableContainer>
        ) : (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <Text variant="h3">No assets yet</Text>
              <Text variant="bodySmall" color="muted" className="mt-1">
                Upload logos and avatars for this client
              </Text>
            </div>
          </div>
        )}
      </PageSection.Card>

      <AssetUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        clientId={clientId}
        onUploadComplete={() => setUploadModalOpen(false)}
      />
    </PageLayout>
  );
};
